/**
 * Modal used to display service contractual terms.
 *
 * Behaviors vary depending on the page:
 * - **PDP (Product Detail Page)**:
 *    - "Saiba mais" (Learn more): opens read-only modal with no action buttons.
 *    - Installation switch: opens modal with checkbox and selection button
 *      (does not add to cart, only changes state).
 * - **PDS (Service Detail Page)**:
 *    - "Confirm" button inside the modal adds the service directly to the cart.
 */

'use client';

import { useMemo } from 'react';

import { Dialog, Spacer } from '@backyard-ui/core';

import { TermsAccordion } from './TermsAccordion';

export type Characteristic = { name: string; value: string };

type AcceptanceTerm = {
  title: string;
  message: string;
};

type BaseProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Footer: optional (checkbox + buttons etc.) */
  footer?: React.ReactNode;

  /** Terms data: can come via acceptanceTerms (PDP) or characteristics(PDS) */
  acceptanceTerms?: AcceptanceTerm[];
  characteristics?: Characteristic[];
};

function toCharacteristics(
  acceptanceTerms?: AcceptanceTerm[],
  characteristics?: Characteristic[]
): Characteristic[] {
  if (characteristics?.length) return characteristics;
  if (acceptanceTerms?.length) {
    return acceptanceTerms.map<Characteristic>((t) => ({
      name: t.title,
      value: t.message,
    }));
  }
  return [];
}

function ServiceContractModalBase(props: Readonly<BaseProps>) {
  const {
    isOpen,
    onOpenChange,
    title = 'É importante você saber',
    footer,
    acceptanceTerms,
    characteristics,
  } = props;

  const normalizedTerms = useMemo(
    () => toCharacteristics(acceptanceTerms, characteristics),
    [acceptanceTerms, characteristics]
  );

  return (
    <Dialog.Root isOpen={isOpen} onOpenChange={onOpenChange} hasScroll>
      <Dialog.Content>
        <Dialog.CloseButton />
        <Spacer axis="vertical" size="4" />
        <Dialog.Title>{title}</Dialog.Title>
        <Spacer axis="vertical" size="4" />

        <Dialog.Description>
          <div className="px-2">
            <TermsAccordion characteristicsArray={normalizedTerms} />
          </div>
        </Dialog.Description>

        {footer && <Dialog.Footer>{footer}</Dialog.Footer>}
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default ServiceContractModalBase;
